import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, UserPlus } from "lucide-react";
import { useState } from "react";

import {
  Button,
  Card,
  EmptyState,
  Pill,
  SearchInput,
  SkeletonRows,
} from "@/components/ui";
import { formatMoney } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";
import {
  customersRepository,
  type Customer,
} from "@/repositories/customersRepository";
import { useAuthStore } from "@/stores/authStore";

import { CustomerDetailDialog } from "./CustomerDetailDialog";
import { NewCustomerDialog } from "./NewCustomerDialog";

/**
 * The people a shop knows by name.
 *
 * Built around the question a shopkeeper actually opens it with — "what does
 * this person owe me" — so the balance is on the list row rather than one tap
 * away. A list that answered it only after a second tap would be a list nobody
 * uses; they would go back to the exercise book.
 *
 * Everything here is **network-only**, unlike the till. A credit limit checked
 * against a local copy is a limit that may already have been spent at another
 * counter, and §4.8 makes credit online-only for exactly that reason.
 */
export function CustomersPage() {
  const [term, setTerm] = useState("");
  const [onAccount, setOnAccount] = useState(false);
  const [adding, setAdding] = useState(false);
  const [opened, setOpened] = useState<string | null>(null);
  const debounced = useDebounced(term, 250);

  const canManage = useAuthStore((s) => s.can("customers.manage"));
  const queryClient = useQueryClient();

  const customers = useQuery({
    queryKey: ["customers", debounced, onAccount],
    queryFn: () => customersRepository.list({ q: debounced, onAccount }),
    placeholderData: (previous) => previous,
  });

  const rows = customers.data ?? [];
  const owed = rows.reduce(
    (sum, row) => sum + Number(row.account?.balance ?? 0),
    0,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Name or phone number"
          containerClassName="w-full flex-1 sm:w-auto sm:min-w-[16rem]"
        />

        <Button
          variant={onAccount ? "primary" : "ghost"}
          onClick={() => setOnAccount((current) => !current)}
        >
          On the book
        </Button>

        {canManage && (
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus size={16} aria-hidden /> Add customer
          </Button>
        )}
      </div>

      {onAccount && rows.length > 0 && (
        <p className="text-sm text-ink-600">
          {rows.length} {rows.length === 1 ? "account" : "accounts"} ·{" "}
          {formatMoney(owed.toFixed(2))} outstanding
        </p>
      )}

      <Card>
        {customers.isLoading ? (
          <SkeletonRows rows={8} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title={term ? "Nobody by that name" : "No customers yet"}
            description={
              term
                ? "Try a phone number, or part of a name."
                : "Add the people who buy on account, or whose purchases you want to be able to look up."
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((customer) => (
              <CustomerRow
                key={customer.id}
                customer={customer}
                onOpen={() => setOpened(customer.id)}
              />
            ))}
          </ul>
        )}
      </Card>

      {adding && (
        <NewCustomerDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            void queryClient.invalidateQueries({ queryKey: ["customers"] });
          }}
        />
      )}

      {opened && (
        <CustomerDetailDialog id={opened} onClose={() => setOpened(null)} />
      )}
    </div>
  );
}

function CustomerRow({
  customer,
  onOpen,
}: {
  customer: Customer;
  onOpen: () => void;
}) {
  const account = customer.account;

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-wrap items-center gap-3 py-3 text-left hover:bg-surface-2"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-ink-900">
            {customer.name}
          </span>
          <span className="block text-xs text-ink-500">
            {customer.phone || "No number on file"}
            {customer.loyalty_points > 0 &&
              ` · ${customer.loyalty_points} points`}
          </span>
        </span>

        {account ? (
          <span className="flex items-center gap-2">
            {/* The figure a shopkeeper is looking for, and the one that decides
                whether they hand over goods. Everything else on this row is
                context for it. */}
            <span className="text-sm font-medium tabular-nums text-ink-900">
              {formatMoney(account.balance)}
            </span>
            {!account.is_active ? (
              <Pill tone="danger">Suspended</Pill>
            ) : account.is_over_limit ? (
              <Pill tone="danger">Over limit</Pill>
            ) : (
              <Pill tone="neutral">
                {formatMoney(account.available_credit)} left
              </Pill>
            )}
          </span>
        ) : (
          <span className="text-xs text-ink-500">Cash only</span>
        )}
      </button>
    </li>
  );
}
